import React from "react";
import styled from "styled-components";
import FullScreenOverlay from "../../common/FullScreenOverlay";
// import EditableAvatar from "../../common/EditableAvatar";
import Avatar from "../../common/Avatar";
import IconButton from "../../common/Button";
import { DeleteIcon } from "../../common/Icons";

/**
 * AccountModal / UserDetailModal
 * - isOpen: boolean to show/hide
 * - onClose: function to close
 * - user: the selected user object
 * - onRemoveUser: callback for "Remove this user"
 * - onResetPassword: callback for "Reset password"
 */
const UserDetail = ({
  isOpen,
  onClose,
  user,
  onRemoveUser,
  onResetPassword,
}) => {
  if (!isOpen) return null;

  // Safeguard if some fields are undefined:
  const { name, email, is_admin, is_active_officer, mdt_roles } = user || {};

  // If mdt_roles might be an array, safely map or join them:
  const mdtRolesArray = Array.isArray(mdt_roles)
    ? mdt_roles
    : mdt_roles
    ? [mdt_roles]
    : [];

  return (
    <FullScreenOverlay
      title="User details"
      onClose={onClose}
      width="480px"
      height="auto"
      isPopup={false}
      iconClose={true}
      isSlimModal={true}
      footerButtons={[
        {
          label: "Cancel",
          onClick: () => onClose?.(),
          variant: "secondary",
        },
        {
          label: "Reset password",
          onClick: () => onResetPassword?.(user),
          variant: "primary",
        },
      ]}
    >
      <Content>
        {/* Profile Section */}
        <ProfileSection>
          {/* <EditableAvatar user={user} /> */}
          <Avatar
            name={name}
            profilePictureUrl={user?.profile_picture}
            size={100}
            bgColor="#2DC4CF"
            textColor="#ffffff"
          />
          <UserInfo>
            <UserName>{name}</UserName>
            <UserEmail>{email}</UserEmail>
            <RoleList>
              {is_admin && <RoleBadge>Admin</RoleBadge>}
              {is_active_officer && <RoleBadge>Active officer</RoleBadge>}
            </RoleList>
          </UserInfo>
        </ProfileSection>

        {/* MDT Roles */}
        <RoleSection>
          <SectionHeader>MDT role</SectionHeader>
          <RoleList>
            {mdtRolesArray.length > 0 ? (
              mdtRolesArray.map((role, idx) => (
                <MdtRoleBadge key={idx}>{role}</MdtRoleBadge>
              ))
            ) : (
              <NoRole>No MDT roles</NoRole>
            )}
          </RoleList>
        </RoleSection>

        <IconButtonStyled
          variant="ghost"
          noPadding={true}
          isRemove={true}
          icon={<DeleteIcon color="#EC2D30" />}
          onClick={() => onRemoveUser?.(user)}
        >
          Remove this user
        </IconButtonStyled>
      </Content>
    </FullScreenOverlay>
  );
};

export default UserDetail;

/*----------------- Styled Components -----------------*/

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 16px;
`;

const ProfileSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 16px;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const UserName = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #000;
`;

const UserEmail = styled.div`
  font-size: 14px;
  color: #666;
`;

const RoleSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
`;

const SectionHeader = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #000;
`;

const RoleList = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 4px;
`;

const RoleBadge = styled.div`
  display: flex;
  padding: 4px 8px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 100px;
  border: 1px solid var(--Colors-Secondary-400, #5bd1da);
  background: var(--Colors-Secondary-100, #e6f8f9);
  color: var(--Colors-Secondary-700, #196e74);
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px; /* 133.333% */
`;

const MdtRoleBadge = styled.div`
  display: flex;
  padding: 8px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 100px;
  background: var(--Colors-Neutral-300, #e9ecef);
  color: var(--Colors-Neutral-600, #6c757d);
  /* Body Text/Regular/Medium */
  font-family: Onest;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px; /* 125% */
`;

const NoRole = styled.div`
  font-size: 14px;
  color: #999;
`;

const IconButtonStyled = styled(IconButton)`
  margin-top: 12px;

  &:hover {
    opacity: 0.8;
  }
`;
